import React from "react";
import dayjs from "dayjs";
import { Badge, Box, Table, Tbody, Td, Text, Th, Thead, Tr, TableContainer } from "@chakra-ui/react";
import {
    TIER_LABELS,
    TIER_COLORS,
    STATUS_LABELS,
    STATUS_COLORS,
    PROVIDER_LABELS,
    PROVIDER_COLORS,
    DEFAULT_CHIP_COLOR,
} from "./subscriptionsConstants";

/**
 * Returns style props for table header cells.
 * @param {boolean} isMobile - Whether the viewport is mobile-sized
 * @returns {Object} Chakra style props for header cells
 */
function headCellProps(isMobile) {
    return {
        background: "rgba(255, 255, 255, 0.06)",
        borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
        color: "rgba(255, 255, 255, 0.75)",
        fontWeight: 600,
        fontSize: isMobile ? "0.65rem" : "0.7rem",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        padding: isMobile ? "10px 12px" : "12px 14px",
    };
}

/**
 * Returns style props for table body cells.
 * @param {boolean} isMobile - Whether the viewport is mobile-sized
 * @returns {Object} Chakra style props for body cells
 */
function bodyCellProps(isMobile) {
    return {
        borderBottom: "1px solid rgba(255, 255, 255, 0.06)",
        color: "rgba(255, 255, 255, 0.85)",
        fontSize: isMobile ? "0.75rem" : "0.8rem",
        padding: isMobile ? "10px 12px" : "12px 14px",
    };
}

/**
 * Builds chip style props from a color scheme.
 * @param {Object} scheme - Color scheme with bg, color, border
 * @returns {Object} Chakra style props
 */
function chipProps(scheme) {
    return {
        background: scheme.bg,
        color: scheme.color,
        border: `1px solid ${scheme.border}`,
        fontWeight: 600,
        fontSize: "0.7rem",
        px: 2,
        borderRadius: "full",
        textTransform: "none",
    };
}

/**
 * Renders a colored chip for a subscription tier.
 * @param {Object} props
 * @param {string} props.tier - Tier key (BLUE, VIOLET, SILVER_STAR, VIP)
 * @returns {React.ReactElement}
 */
function TierChip({ tier }) {
    const scheme = TIER_COLORS[tier] || DEFAULT_CHIP_COLOR;
    const label = TIER_LABELS[tier] || tier || "—";
    return <Badge {...chipProps(scheme)}>{label}</Badge>;
}

/**
 * Renders a colored chip for a subscription status.
 * @param {Object} props
 * @param {string} props.status - Status key (active, pending, expired, cancelled)
 * @returns {React.ReactElement}
 */
function StatusChip({ status }) {
    const scheme = STATUS_COLORS[status] || DEFAULT_CHIP_COLOR;
    const label = STATUS_LABELS[status] || status || "—";
    return <Badge {...chipProps(scheme)}>{label}</Badge>;
}

/**
 * Renders a colored chip for the payment provider (Stripe / OM prepaid).
 * @param {Object} props
 * @param {string} props.provider - Provider key (stripe, orange_money)
 * @returns {React.ReactElement}
 */
function ProviderChip({ provider }) {
    const scheme = PROVIDER_COLORS[provider] || DEFAULT_CHIP_COLOR;
    const label = PROVIDER_LABELS[provider] || provider || "—";
    return <Badge {...chipProps(scheme)}>{label}</Badge>;
}

/**
 * Formats a date string as DD/MM/YYYY or "—" if missing/invalid.
 * @param {string} value - ISO date string
 * @returns {string}
 */
function formatDate(value) {
    if (!value) return "—";
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("DD/MM/YYYY") : "—";
}

/**
 * Formats an amount with its currency code, or "—" if missing.
 * @param {number|null|undefined} amount - Numeric amount
 * @param {string} currency - Currency code
 * @returns {string}
 */
function formatAmount(amount, currency) {
    if (amount === null || amount === undefined) return "—";
    return `${amount} ${currency || ""}`.trim();
}

/**
 * Renders a single subscription row.
 * @param {Object} props
 * @param {Object} props.sub - Subscription object from the API
 * @param {boolean} props.isMobile - Whether the viewport is mobile-sized
 * @param {Function} props.onSelect - Row click handler (receives subscription id)
 * @returns {React.ReactElement}
 */
function SubscriptionRow({ sub, isMobile, onSelect }) {
    const cellProps = bodyCellProps(isMobile);
    const handleClick = () => onSelect?.(sub.id);
    return (
        <Tr onClick={handleClick}
            cursor={onSelect ? "pointer" : "default"} _hover={{ background: "rgba(255, 255, 255, 0.04)" }}>
            <Td {...cellProps}><TierChip tier={sub.subscription_type} /></Td>
            <Td {...cellProps}><StatusChip status={sub.status} /></Td>
            <Td {...cellProps}><ProviderChip provider={sub.payment_provider} /></Td>
            <Td {...cellProps}>{formatDate(sub.start_date)}</Td>
            <Td {...cellProps}>{formatDate(sub.end_date)}</Td>
            <Td {...cellProps}>{formatAmount(sub.amount, sub.currency)}</Td>
            <Td {...cellProps}>{sub.auto_renew ? "Oui" : "Non"}</Td>
        </Tr>
    );
}

/**
 * Renders the table header row with column titles.
 * @param {Object} props
 * @param {boolean} props.isMobile - Whether the viewport is mobile-sized
 * @returns {React.ReactElement}
 */
function SubscriptionsHead({ isMobile }) {
    const props = headCellProps(isMobile);
    return (
        <Thead>
            <Tr>
                <Th {...props}>Type</Th>
                <Th {...props}>Statut</Th>
                <Th {...props}>Paiement</Th>
                <Th {...props}>Du</Th>
                <Th {...props}>Au</Th>
                <Th {...props}>Montant</Th>
                <Th {...props}>Auto-renouv.</Th>
            </Tr>
        </Thead>
    );
}

const containerProps = {
    background: "rgba(255, 255, 255, 0.02)",
    border: "1px solid rgba(255, 255, 255, 0.08)",
    borderRadius: "8px",
    overflow: "hidden",
};

const noResultsProps = {
    p: 6,
    textAlign: "center",
    color: "rgba(255, 255, 255, 0.5)",
    fontSize: "0.85rem",
};

/**
 * Renders the subscriptions table or a "no results" message.
 * @param {Object} props
 * @param {Array} props.subscriptions - List of subscription objects
 * @param {boolean} props.isMobile - Whether the viewport is mobile-sized
 * @param {Function} props.onSelect - Row click handler (receives subscription id)
 * @returns {React.ReactElement}
 */
function SubscriptionsTable({ subscriptions, isMobile, onSelect }) {
    if (!subscriptions || subscriptions.length === 0) {
        return (
            <Box {...containerProps}>
                <Text {...noResultsProps}>Aucun abonnement</Text>
            </Box>
        );
    }
    return (
        <TableContainer {...containerProps}>
            <Table size={isMobile ? "sm" : "md"}>
                <SubscriptionsHead isMobile={isMobile} />
                <Tbody>
                    {subscriptions.map((sub) => (
                        <SubscriptionRow key={sub.id} sub={sub} isMobile={isMobile} onSelect={onSelect} />
                    ))}
                </Tbody>
            </Table>
        </TableContainer>
    );
}

export default SubscriptionsTable;
